import React, { useEffect, useRef, useState } from 'react';
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion';

const DISMISS_DISTANCE_PX = 90; // drag past this far down and releasing dismisses the sheet
const DISMISS_VELOCITY_PX_MS = 0.5; // or a fast-enough flick, regardless of distance
export const MOBILE_SHEET_ANIMATION_MS = 280;

// A bottom sheet that overlays the bottom of its (relatively positioned)
// parent: slides up from the bottom when opened, and can be dragged back
// down from its top handle to dismiss (the handle also works as a plain tap
// target). Fully controlled via `isOpen` — flipping it drives the same
// slide animation as a drag-dismiss does, unless the user prefers reduced
// motion, in which case it opens/closes instantly.
//
// `onDismiss` fires once when a drag-down gesture crosses the dismiss
// threshold (or the handle is tapped/activated) — the caller owns actually
// flipping `isOpen` to false afterward, so content can keep rendering
// through a graceful close animation instead of going blank mid-slide.
const MobileSheet = ({ isOpen, onDismiss, tokens, ariaLabel = 'Close panel (drag down or press)', onHeightChange, children }) => {
    const prefersReducedMotion = usePrefersReducedMotion();
    const [dragY, setDragY] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const touchStartY = useRef(0);
    const touchStartTime = useRef(0);
    const panelRef = useRef(null);

    // Reset any leftover drag offset whenever openness is driven externally.
    useEffect(() => {
        setDragY(0);
        setIsDragging(false);
    }, [isOpen]);

    // Report how much vertical space the sheet occupies while open (0 when
    // closed), so the layer underneath can pad its scroll area and let any
    // row scroll up clear of the sheet. Content height varies per event, so
    // this tracks the rendered box rather than assuming a fixed height.
    useEffect(() => {
        if (!onHeightChange) return undefined;
        if (!isOpen || !panelRef.current) {
            onHeightChange(0);
            return undefined;
        }
        const el = panelRef.current;
        onHeightChange(el.offsetHeight);
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(() => onHeightChange(el.offsetHeight));
        observer.observe(el);
        return () => observer.disconnect();
    }, [isOpen, onHeightChange]);

    const handleTouchStart = (e) => {
        touchStartY.current = e.touches[0].clientY;
        touchStartTime.current = Date.now();
        setIsDragging(true);
    };

    const handleTouchMove = (e) => {
        if (!isDragging) return;
        const delta = e.touches[0].clientY - touchStartY.current;
        // Only track downward movement — dragging up shouldn't overshoot past open.
        setDragY(Math.max(0, delta));
    };

    const handleTouchEnd = () => {
        if (!isDragging) return;
        setIsDragging(false);

        const elapsedMs = Math.max(1, Date.now() - touchStartTime.current);
        const velocity = dragY / elapsedMs;
        const shouldDismiss = dragY > DISMISS_DISTANCE_PX || (dragY > 20 && velocity > DISMISS_VELOCITY_PX_MS);

        if (shouldDismiss) {
            const panelHeight = panelRef.current ? panelRef.current.offsetHeight : window.innerHeight;
            setDragY(panelHeight);
            if (onDismiss) onDismiss();
        } else {
            // Under the threshold — spring back open.
            setDragY(0);
        }
    };

    const grabberColor = tokens ? tokens.colors.faint : '#9ca3af';
    const surface = tokens ? tokens.colors.surface : '#fff';
    const gridLine = tokens ? tokens.colors.gridLine : '#e5e7eb';

    return (
        <div
            ref={panelRef}
            className={`absolute inset-x-0 bottom-0 flex flex-col ${isOpen ? '' : 'pointer-events-none'}`}
            style={{
                maxHeight: '82%',
                // Without this, long content (e.g. a lengthy description) doesn't
                // reliably get clamped to the flex-1 content area below and just
                // spills out past this box's own max-height instead of scrolling
                // inside it — which inflates the page's real layout size and can
                // drag the whole page's scroll position along with it once
                // something (e.g. scrollIntoView) tries to bring it into view.
                overflow: 'hidden',
                background: surface,
                borderTop: `1px solid ${gridLine}`,
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                boxShadow: '0 -8px 24px rgba(0,0,0,0.18)',
                transform: isOpen ? `translateY(${dragY}px)` : 'translateY(100%)',
                transition: (isDragging || prefersReducedMotion)
                    ? 'none'
                    : `transform ${MOBILE_SHEET_ANIMATION_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`,
                zIndex: 5,
            }}
            aria-hidden={!isOpen}
        >
            {/* Drag handle — also a tap target for anyone who'd rather not drag */}
            <div
                className="flex-none flex items-center justify-center"
                style={{ height: 26, cursor: 'grab', touchAction: 'none' }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchEnd}
                onClick={onDismiss}
                role="button"
                tabIndex={isOpen ? 0 : -1}
                aria-label={ariaLabel}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (onDismiss) onDismiss();
                    }
                }}
            >
                <span style={{ width: 36, height: 5, borderRadius: 3, background: grabberColor, opacity: 0.6 }} aria-hidden="true" />
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
                {children}
            </div>
        </div>
    );
};

export default MobileSheet;
